<template>
  <div>
    <md-card>
      <md-card-header>
        <div class="md-layout">
          <div>
            <h4 class="title">Summary</h4>
            <p class="category">
              Quick overview of ticket allocation and discount usage for this
              departure.
            </p>
          </div>
        </div>
      </md-card-header>
      <md-card-content class="summary-content">
        <template v-if="!isTour">
          <div class="summary-section summary-section--first">
            <div
              class="md-layout summary-row md-alignment-center"
              style="justify-content: space-between;"
            >
              <div>
                <h6>Total tickets sold</h6>
                <div class="md-caption">Amount available</div>
              </div>
              <div class="text-right">
                <div class="summary-number">
                  {{ totalSold }} / {{ totalAvailableLabel }}
                </div>
              </div>
            </div>
          </div>

          <md-divider />

          <div class="summary-section">
            <h6>Bookings by Ticket Type</h6>
            <div class="md-layout summary-header">
              <div class="md-layout-item md-size-45">Ticket type</div>
              <div class="md-layout-item md-size-25 text-right">
                Sold / Allocation
              </div>
              <div class="md-layout-item md-size-20 text-right">Discounted</div>
              <div class="md-layout-item md-size-10"></div>
            </div>
            <template v-if="ticketRows.length">
              <div v-for="row in ticketRows" :key="row.key">
                <div class="summary-row md-layout md-alignment-center">
                  <div class="md-layout-item md-size-45">
                    {{ row.label }}
                  </div>
                  <div class="md-layout-item md-size-25 text-right">
                    {{ row.sold }} / {{ row.allocationLabel }}
                  </div>
                  <div class="md-layout-item md-size-20 text-right">
                    {{ row.discountedLabel }}
                  </div>
                  <div class="md-layout-item md-size-10 summary-arrow">
                    <md-button
                      v-if="row.bookingIds.length"
                      class="md-just-icon md-simple md-primary summary-toggle"
                      @click.prevent="toggleTicketBookings(row.key)"
                    >
                      <md-icon>{{
                        isTicketExpanded(row.key)
                          ? "keyboard_arrow_up"
                          : "keyboard_arrow_down"
                      }}</md-icon>
                      <md-tooltip md-direction="top">
                        {{ isTicketExpanded(row.key) ? "Hide" : "Show" }}
                        bookings ({{ row.bookingIds.length }})
                      </md-tooltip>
                    </md-button>
                  </div>
                </div>
                <collapse-transition>
                  <div
                    v-show="isTicketExpanded(row.key)"
                    class="summary-bookings"
                  >
                    <div
                      class="summary-booking"
                      v-for="bookingId in row.bookingIds"
                      :key="bookingId"
                    >
                      <router-link
                        :to="{
                          name: 'Booking Profile',
                          params: { id: bookingId }
                        }"
                      >
                        Booking #{{ bookingId }}
                      </router-link>
                    </div>
                  </div>
                </collapse-transition>
              </div>
            </template>
            <div v-else class="md-caption">No tickets booked yet.</div>
          </div>

          <md-divider />
        </template>

        <div class="summary-section">
          <h6>Bookings by Discount{{ isTour ? " Code" : "" }}</h6>
          <div class="md-layout summary-header">
            <div class="md-layout-item md-size-65">Discount</div>
            <div class="md-layout-item md-size-25 text-right">Bookings</div>
            <div class="md-layout-item md-size-10"></div>
          </div>
          <template v-if="discountRows.length">
            <div v-for="row in discountRows" :key="row.label">
              <div class="summary-row md-layout md-alignment-center">
                <div class="md-layout-item md-size-65">{{ row.label }}</div>
                <div class="md-layout-item md-size-25 text-right">
                  {{ row.count }}
                </div>
                <div class="md-layout-item md-size-10 summary-arrow">
                  <md-button
                    v-if="row.bookingIds.length"
                    class="md-just-icon md-simple md-primary summary-toggle"
                    @click.prevent="toggleDiscountBookings(row.key)"
                  >
                    <md-icon>{{
                      isDiscountExpanded(row.key)
                        ? "keyboard_arrow_up"
                        : "keyboard_arrow_down"
                    }}</md-icon>
                    <md-tooltip md-direction="top">
                      {{ isDiscountExpanded(row.key) ? "Hide" : "Show" }}
                      bookings ({{ row.bookingIds.length }})
                    </md-tooltip>
                  </md-button>
                </div>
              </div>
              <collapse-transition>
                <div
                  v-show="isDiscountExpanded(row.key)"
                  class="summary-bookings summary-bookings--discount"
                >
                  <div
                    class="summary-booking"
                    v-for="bookingId in row.bookingIds"
                    :key="bookingId"
                  >
                    <router-link
                      :to="{
                        name: 'Booking Profile',
                        params: { id: bookingId }
                      }"
                    >
                      Booking #{{ bookingId }}
                    </router-link>
                  </div>
                </div>
              </collapse-transition>
            </div>
          </template>
          <div v-else class="md-caption">No discount bookings yet.</div>
        </div>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { CollapseTransition } from "vue2-transitions";

export default {
  props: ["departure"],
  components: { CollapseTransition },

  data() {
    return {
      expandedTickets: {},
      expandedDiscounts: {}
    };
  },

  computed: {
    ...mapGetters("departures", [
      "isTour",
      "totalSold",
      "totalAvailableLabel",
      "ticketRows",
      "discountRows"
    ])
  },

  methods: {
    loadSummary() {
      if (!this.departure?.id) return;
      this.$store.dispatch("departures/fetchSummary", this.departure.id);
    },
    toggleTicketBookings(key) {
      this.$set(this.expandedTickets, key, !this.isTicketExpanded(key));
    },
    toggleDiscountBookings(key) {
      this.$set(this.expandedDiscounts, key, !this.isDiscountExpanded(key));
    },
    isTicketExpanded(key) {
      return Boolean(this.expandedTickets[key]);
    },
    isDiscountExpanded(key) {
      return Boolean(this.expandedDiscounts[key]);
    }
  },

  mounted() {
    this.loadSummary();
  },

  watch: {
    "departure.id"(nextId, prevId) {
      if (nextId && nextId !== prevId) {
        this.loadSummary();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.summary-row {
  padding: 3px 0;
  align-items: center;
}

.summary-row h6 {
  margin: 0;
}

.summary-content {
  padding-top: 8px;
}

.summary-number {
  font-weight: 600;
}

.summary-header {
  padding: 4px 0;
  color: #777;
  font-size: 0.85rem;
  text-transform: uppercase;
}

.summary-section {
  margin-top: 0.75rem;
}

.summary-section--first {
  margin-top: 0;
}

.summary-bookings {
  padding: 6px 0 10px 18px;
}

.summary-booking {
  padding: 4px 0;
  border-bottom: 1px solid #d2d2d2;
}

.summary-booking:last-child {
  border-bottom: 0;
}

.summary-toggle {
  width: 24px;
  height: 24px;
  margin: 0;
}

.summary-arrow {
  display: flex;
  justify-content: flex-end;
}

.summary-bookings--discount {
  padding-left: 18px;
}
</style>
